import { useMemo } from "react";
import type { Identifier } from "ra-core";
import { useGetList } from "ra-core";

/**
 * How many ranked contacts to pull. The pass only ever scores a shortlist —
 * the point is a good top of the queue, not a total ordering of 12,898 people.
 */
const MAX_RANKED = 200;

export interface SwipeSuggestion {
  id: Identifier;
  contact_id: Identifier;
  scenario: string;
  score: number;
  suggested_value: string | null;
  /** Why, in the operator's terms. Shown on the card. */
  rationale: string;
  /** What the pass actually read, so a bad call is traceable. */
  evidence: string | null;
}

export interface SwipeSuggestions {
  /** Contact ids, best first. Empty when the scenario has had no pass. */
  orderedIds: Identifier[];
  byContact: Map<Identifier, SwipeSuggestion>;
  isPending: boolean;
}

/**
 * The first-pass recommendations for a scenario.
 *
 * These order the deck and put a reason on the card, so the question becomes
 * "is this judgement right?" rather than "who is this person?". They are
 * advisory only: nothing here is written to a contact without a swipe, and a
 * scenario with no pass simply falls back to its own filter.
 */
export function useSwipeSuggestions(scenarioId: string): SwipeSuggestions {
  const { data, isPending } = useGetList<SwipeSuggestion>(
    "contact_triage_suggestions",
    {
      pagination: { page: 1, perPage: MAX_RANKED },
      sort: { field: "score", order: "DESC" },
      filter: { scenario: scenarioId },
    },
  );

  return useMemo(() => {
    const rows = data ?? [];
    return {
      orderedIds: rows.map((row) => row.contact_id),
      byContact: new Map(rows.map((row) => [row.contact_id, row])),
      isPending,
    };
  }, [data, isPending]);
}
